import { useEffect, useState } from "react";
import axios from "axios";
import { X, Plus } from "lucide-react";
import { API, formatINR } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { toast } from "sonner";
import Portal from "@/components/Portal";

// Full-screen modal that shows all fabric or wood swatches so the shopper
// can add multiple ₹50 samples to their cart from any product page.
export default function SwatchPickerModal({ open, onClose, kind, productName }) {
  const [swatches, setSwatches] = useState([]);
  const [selected, setSelected] = useState({});
  const [loading, setLoading] = useState(false);
  const { add } = useCart();

  useEffect(() => {
    if (!open) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    axios
      .get(`${API}/products?category=swatches&limit=60`)
      .then((r) => setSwatches(r.data))
      .finally(() => setLoading(false));
  }, [open]);

  if (!open) return null;

  const filtered = swatches.filter((s) => s.swatch_kind === kind);
  const count = Object.values(selected).filter(Boolean).length;
  const total = count * 50;

  const toggle = (slug) => setSelected((prev) => ({ ...prev, [slug]: !prev[slug] }));

  const addAll = () => {
    const items = filtered.filter((s) => selected[s.slug]);
    if (!items.length) return;
    items.forEach((s) => {
      add({
        product_id: s.id,
        quantity: 1,
        customization: {},
        unit_price: 50,
        name: s.name,
        image: s.images?.[0],
      });
    });
    toast.success(`${items.length} ${kind} swatch${items.length > 1 ? "es" : ""} added to cart`);
    setSelected({});
    onClose();
  };

  const heading = kind === "fabric" ? "Fabric Swatches" : "Wood Swatches";
  const sub = kind === "fabric"
    ? "10cm × 10cm fabric samples — feel the weave and weight."
    : "Finished wood samples — see the true grain and tone at home.";

  return (
    <Portal>
      <div
        className="fixed inset-0 z-[75] bg-[#0B0B0C]/95 overflow-y-auto"
        data-testid={`swatch-modal-${kind}`}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div
          className="w-full max-w-4xl mx-auto my-6 p-6 md:p-10"
          style={{ background: "#141414", border: "1px solid var(--hairline)" }}
        >
          <div className="flex justify-between items-start mb-8 gap-4">
            <div>
              <div className="font-micro text-[#B8895C] mb-2">
                Order Swatches · ₹50 each{productName ? ` · for ${productName}` : ""}
              </div>
              <h2 className="font-serif text-[#EDE4D3] text-3xl md:text-4xl">{heading}</h2>
              <p className="text-[#8A8680] mt-3 max-w-lg leading-relaxed">{sub}</p>
            </div>
            <button
              onClick={onClose}
              className="text-[#EDE4D3] hover:text-[#B8895C] transition-colors duration-500"
              data-testid={`swatch-modal-close-${kind}`}
              aria-label="Close"
            >
              <X size={20} strokeWidth={1} />
            </button>
          </div>

          {loading ? (
            <div className="py-16 text-center font-micro text-[#8A8680]">Loading swatches…</div>
          ) : filtered.length === 0 ? (
            <div className="py-16 text-center font-serif text-[#8A8680]">No swatches available yet.</div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid={`swatch-modal-grid-${kind}`}>
              {filtered.map((s) => {
                const on = !!selected[s.slug];
                return (
                  <button
                    key={s.slug}
                    onClick={() => toggle(s.slug)}
                    className="text-left transition-all duration-500"
                    style={{ background: "#0B0B0C", border: `1px solid ${on ? "#B8895C" : "var(--hairline)"}` }}
                    data-testid={`swatch-modal-item-${s.slug}`}
                  >
                    <div className="aspect-square" style={{ background: s.swatch_hex || "#141414" }}>
                      {s.images?.[0] && (
                        <img src={s.images[0]} alt={s.name} className="w-full h-full object-cover" loading="lazy" />
                      )}
                    </div>
                    <div className="p-3">
                      <div className="font-serif text-[#EDE4D3] text-sm leading-tight">{s.name.replace(" Swatch", "")}</div>
                      <div className="flex justify-between items-center mt-2">
                        <div className="font-micro text-[#8A8680]">₹50</div>
                        <div className={`font-micro ${on ? "text-[#B8895C]" : "text-[#8A8680]"}`}>
                          {on ? "✓ Added" : "+ Add"}
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}

          <div className="mt-8 pt-6 flex flex-col md:flex-row md:items-center justify-between gap-4" style={{ borderTop: "1px solid var(--hairline)" }}>
            <div className="font-micro text-[#8A8680]" data-testid={`swatch-modal-summary-${kind}`}>
              {count > 0 ? `${count} selected · ${formatINR(total)}` : "Tap a swatch to select"}
            </div>
            <div className="flex gap-3">
              <button onClick={onClose} className="btn-ghost" data-testid={`swatch-modal-cancel-${kind}`}>Cancel</button>
              <button
                onClick={addAll}
                disabled={count === 0}
                className="btn-bronze"
                style={count === 0 ? { opacity: 0.4, cursor: "not-allowed" } : {}}
                data-testid={`swatch-modal-add-${kind}`}
              >
                <Plus size={12} className="inline mr-2" /> Add {count || ""} to Cart
              </button>
            </div>
          </div>
        </div>
      </div>
    </Portal>
  );
}
